<script setup lang="ts">
import { ref, provide } from "vue";

import Graph from "@/modules/explorer/components/Graphs/Graph.vue";
import DocumentDO = App.Data.Explorer.DocumentDO;
import {
	derivedDocumentKey,
	documentKey,
} from "@/modules/explorer/ExplorerUtils";
import { ApiError, Result } from "@/api/base";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useApiClient } from "@/api/client";
import { processedDocument } from "chartjs-graph";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
const { abortController, abortRequestAndResetController } =
	useAbortController();

const props = defineProps<{
	isLoadingDocument: boolean;
	document: DocumentDO;
	lastUpdated: number;
}>();

const localDocument = ref<DocumentDO>(props.document);
const segmentSizeDerivedDocument = ref<DocumentDO>();

provide(derivedDocumentKey, segmentSizeDerivedDocument);
provide(documentKey, localDocument);

const fetchDataForGraph = async (): Promise<Result<any, ApiError>> => {
	abortRequestAndResetController();

	return await promptInvalidPermissions(
		useApiClient("segments").fetchDataForDocument(
			processedDocument(props.document),
			"segments/",
			abortController.value?.signal ?? null
		)
	);
};

const isLoadingGraphData = ref<boolean>(false);
</script>

<template>
	<div class="h-100 w-100" style="position: relative">
		<graph
			v-if="!props.isLoadingDocument"
			@update:is-loading="(value) => (isLoadingGraphData = value)"
			@update:derived-document="(value:DocumentDO) => (segmentSizeDerivedDocument = value)"
			:fetch-data-for-graph="fetchDataForGraph"
			:last-updated="props.lastUpdated"
		/>
		<div
			v-if="isLoadingGraphData"
			class="h-100 w-100 d-flex justify-center align-center"
			style="position: absolute; top: 0; left: 0"
		>
			<v-progress-circular color="primary" indeterminate />
		</div>
	</div>
</template>

<style scoped lang="scss"></style>
